import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { MenuItemCard } from '@/components/ui/MenuItemCard';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select';
import { menuService } from '../../services/menuService';
import type { MenuItemScheduleResponse } from '../../types/menuItemTypes';

interface MenuSectionProps {
  /** Event booking category ID to fetch menu items for */
  eventBookingCategoryId: string;
  /** Currency code for formatting prices */
  currency: string;
  /** Optional container class */
  className?: string;
}

/**
 * MenuSection manages the fetching and display of menu items for a specific booking category.
 * Includes search and category filtering tailored for desktop view.
 */
export const MenuSection: React.FC<MenuSectionProps> = ({
  eventBookingCategoryId,
  currency,
  className = '',
}) => {
  const [items, setItems] = useState<MenuItemScheduleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const fetchItems = async (isInitial = false) => {
    try {
      if (!isInitial) setLoading(true); // Don't show loading on every initial load if we want it smoother
      
      const response = await menuService.fetchPublicMenuItemSchedules(eventBookingCategoryId, {
        search: search || undefined,
        category: selectedCategory || undefined,
      });
      
      const data = (response.data || [])
        .map(item => item.menu_item_schedule)
        .filter(schedule => !!schedule?.menu_item);
      setItems(data);
      
      // Extract unique categories for filtering on the first load
      if (isInitial || availableCategories.length === 0) {
        const categories = Array.from(new Set(data.map(item => item.menu_item?.category).filter(Boolean))) as string[];
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
      setHasInitialLoad(true);
    }
  };

  // Initial load
  useEffect(() => {
    fetchItems(true);
  }, [eventBookingCategoryId]);

  // Refetch when category changes
  useEffect(() => {
    if (hasInitialLoad) {
      fetchItems();
    }
  }, [selectedCategory]);

  // Debounced search effect
  useEffect(() => {
    if (hasInitialLoad) {
      const timer = setTimeout(() => {
        fetchItems();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [search]);

  // Format category names for display
  const categoryLabels: Record<string, string> = {
    'COCKTAILS': 'Cocktails',
    'SHOTS': 'Shots',
    'BEER_AND_WINE': 'Beer & Wine',
    'SOFT_DRINKS': 'Soft Drinks',
    'MOCKTAILS': 'Mocktails',
    'STARTERS': 'Starters',
    'MAINS': 'Mains',
    'DESSERTS': 'Desserts',
    'SNACKS': 'Snacks',
    'BOTTLE_SERVICE': 'Bottle Service',
  };

  const formatCategoryName = (cat: string | null) => {
    if (!cat) return 'All Categories';
    return categoryLabels[cat] || cat.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Don't render anything if no items and no active filters after initial load
  if (hasInitialLoad && items.length === 0 && !search && !selectedCategory) return null;

  return (
    <section className={`py-8 ${className}`}>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8 mb-10 px-5">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold font-serif text-white tracking-tight">Food & Drinks</h2>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white/[0.02] border border-white/[0.05] p-2 rounded-2xl">
          {/* Category Dropdown */}
          <Select 
            value={selectedCategory || ''} 
            onValueChange={(val) => setSelectedCategory(val || null)}
          >
            <SelectTrigger 
                className="sm:w-48 bg-transparent border-transparent hover:bg-white/5"
                icon={<SlidersHorizontal className="w-3.5 h-3.5" />}
            >
              <SelectValue placeholder="All Categories">
                {formatCategoryName(selectedCategory)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="">All Categories</SelectItem>
                <SelectItem value="COCKTAILS">Cocktails</SelectItem>
                <SelectItem value="SHOTS">Shots</SelectItem>
                <SelectItem value="BEER_AND_WINE">Beer & Wine</SelectItem>
                <SelectItem value="SOFT_DRINKS">Soft Drinks</SelectItem>
                <SelectItem value="MOCKTAILS">Mocktails</SelectItem>
                <SelectItem value="STARTERS">Starters</SelectItem>
                <SelectItem value="MAINS">Mains</SelectItem>
                <SelectItem value="DESSERTS">Desserts</SelectItem>
                <SelectItem value="SNACKS">Snacks</SelectItem>
                <SelectItem value="BOTTLE_SERVICE">Bottle Service</SelectItem>
            </SelectContent>
          </Select>

          {/* Vertical Divider (Desktop Only) */}
          <div className="hidden sm:block w-[1px] h-6 bg-white/10 mx-1" />

          {/* Search Box */}
          <div className="relative group flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent py-2 pl-10 pr-10 text-sm text-white focus:outline-none transition-all placeholder:text-gray-600"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 px-5">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="aspect-square w-full rounded-2xl bg-white/5 animate-pulse border border-white/5" />
          ))}
        </div>
      ) : (
        <AnimatePresence mode="popLayout">
          {items.length > 0 ? (
            <motion.div 
              layout
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 px-5"
            >
              {items.map((item) => (
                <motion.div
                  key={item.menu_item?.id || Math.random()}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <MenuItemCard
                    imageId={item.menu_item?.image_id}
                    name={item.menu_item?.name || 'Unnamed Item'}
                    price={item.price}
                    category={item.menu_item?.category || 'General'}
                    currency={currency}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-32 flex flex-col items-center text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-2">
                <Search className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-gray-400 font-medium text-xl">No items found matching your filters.</p>
              <button 
                onClick={() => {setSearch(''); setSelectedCategory(null);}}
                className="text-blue-500 font-black uppercase text-xs tracking-widest hover:text-blue-400 transition-colors"
              >
                Clear all filters
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </section>
  );
};
